import { FC, useEffect, useState, useMemo, useContext } from 'react';
import { useMediaQuery } from '@mui/material';
import { VendorPageContext } from '../../../context';
import { Table, Pagination, SearchSection, Link } from '../../../components';
import { PurchaseOrdersFilters } from './PurchaseOrdersFilters';
import { useSnackbar } from 'notistack';
import { formatDate, formatMoney } from '../../../helpers';
import { PurchaseOrdersMobile } from './PurchaseOrdersMobile';
import { IPurchaseOrder } from '../../../models';
import { VendorPage } from '../VendorPage';
import { getPurchaseOrders } from '../../../fetch';

interface IPurchaseOrders {}

export const PurchaseOrders: FC<IPurchaseOrders> = () => {
  const isMobile = useMediaQuery('(max-width: 960px)');
  const { enqueueSnackbar } = useSnackbar();
  const { loadingVendors, vendorSageId, jobs, isLoadingJobs } = useContext(VendorPageContext);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingPurchaseOrders, setLoadingPurchaseOrders] = useState<boolean>(true);
  const [purchaseOrders, setPurchaseOrders] = useState<IPurchaseOrder[]>([]);
  const [selectedSort, setSelectedSort] = useState<string>('OrderDate');
  const [sortDirection, setSortDirection] = useState<{
    PurchaseOrderNumber?: 'Asc' | 'Desc';
    Partner?: 'Asc' | 'Desc';
    OrderDate?: 'Asc' | 'Desc';
    Job?: 'Asc' | 'Desc';
    Task?: 'Asc' | 'Desc';
    Notes?: 'Asc' | 'Desc';
    OrderType?: 'Asc' | 'Desc';
    Status?: 'Asc' | 'Desc';
    Subtotal?: 'Asc' | 'Desc';
    Tax?: 'Asc' | 'Desc';
    Total?: 'Asc' | 'Desc';
  }>({
    OrderDate: 'Desc'
  });

  const [searchValue, setSearchValue] = useState('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [filters, setFilters] = useState<{
    jobId?: number | string;
  } | null>({});
  const [selectedJobSageId, setSelectedJobSageId] = useState('');
  const [jobName, setJobName] = useState(null);
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const fetchPurchaseOrders = async () => {
    try {
      setLoadingPurchaseOrders(true);
      const res = await getPurchaseOrders({
        page: page + 1,
        perPage: perPage,
        search: searchedValue,
        partnerSageId: vendorSageId,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        jobId: parseInt(selectedJobSageId) ? parseInt(selectedJobSageId) : null,
        statuses: ['Open', 'Review']
      });
      setRecordCount(res.totalRecordCount);
      setPurchaseOrders(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading purchase orders, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingPurchaseOrders(false);
    }
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };
  useEffect(() => {
    fetchPurchaseOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSageId, filters, sortDirection, selectedSort, page, perPage, searchedValue]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Order No.',
        accessor: 'purchaseOrderNumber',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPurchaseOrder } };
        }) => {
          return <Link to={`/vendors/purchase-orders/${original.purchaseOrderNumber}`}>{original.purchaseOrderNumber}</Link>;
        }
      },
      {
        Header: 'Date',
        accessor: 'orderDate',
        isServerSorted: selectedSort === 'OrderDate',
        isServerSortedDesc: sortDirection.OrderDate === 'Desc',
        handleClickColumn: () => handleClickColumn('OrderDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPurchaseOrder } };
        }) => {
          return <span>{formatDate(original.orderDate)}</span>;
        }
      },
      {
        Header: 'Job',
        accessor: 'job',
        isServerSorted: selectedSort === 'Job',
        isServerSortedDesc: sortDirection.Job === 'Desc',
        handleClickColumn: () => handleClickColumn('Job')
      },
      {
        Header: 'Description',
        accessor: 'description',
        sort: false
      },
      {
        Header: 'Order Type',
        accessor: 'orderType',
        isServerSorted: selectedSort === 'OrderType',
        isServerSortedDesc: sortDirection.OrderType === 'Desc',
        handleClickColumn: () => handleClickColumn('OrderType')
      },
      {
        Header: 'Status',
        accessor: 'status',
        isServerSorted: selectedSort === 'Status',
        isServerSortedDesc: sortDirection.Status === 'Desc',
        handleClickColumn: () => handleClickColumn('Status')
      },
      {
        Header: 'Vendor',
        accessor: 'partner',
        isServerSorted: selectedSort === 'Partner',
        isServerSortedDesc: sortDirection.Partner === 'Desc',
        handleClickColumn: () => handleClickColumn('Partner')
      },
      {
        Header: 'Task',
        accessor: 'task',
        isServerSorted: selectedSort === 'Task',
        isServerSortedDesc: sortDirection.Task === 'Desc',
        handleClickColumn: () => handleClickColumn('Task')
      },
      {
        Header: 'Total',
        accessor: 'total',
        isServerSorted: selectedSort === 'Total',
        isServerSortedDesc: sortDirection.Total === 'Desc',
        handleClickColumn: () => handleClickColumn('Total'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPurchaseOrder } };
        }) => {
          return <span>{formatMoney(original.total)}</span>;
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrders]);
  return (
    <VendorPage>
      <PurchaseOrdersFilters
        jobs={jobs}
        setSelectedJobSageId={setSelectedJobSageId}
        selectedJobSageId={selectedJobSageId}
        setHasAppliedFilters={setHasAppliedFilters}
        hasAppliedFilters={hasAppliedFilters}
        isLoading={isLoadingPurchaseOrders || loadingVendors || isLoadingJobs}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              jobId: ''
            });
            setSelectedJobSageId('');
          } else {
            setPage(0);
            setFilters({
              ...filters,
              jobId: selectedJobSageId || undefined
            });
          }
        }}
        setSearchValue={setSearchValue}
        searchValue={searchValue}
        setJobName={setJobName}
        jobName={jobName}
        setSearchedValue={setSearchedValue}
      />
      {searchedValue && (
        <SearchSection
          searchedValue={searchedValue}
          handleClearSearch={() => {
            setSearchValue('');
            setSearchedValue('');
            setHasAppliedFilters(false);
            setPage(0);
          }}
        />
      )}
      <Table
        columns={columns}
        data={purchaseOrders}
        isLoading={isLoadingPurchaseOrders || loadingVendors}
        hidePagination
        ResponsiveComponent={isMobile ? PurchaseOrdersMobile : undefined}
      />
      {!isLoadingPurchaseOrders && !loadingVendors && <Pagination page={page} count={recordCount} rowsPerPage={perPage} setPage={setPage} setRowsPerPage={setRowsPerPage} />}
    </VendorPage>
  );
};
